import { Col, Form, Row } from 'react-bootstrap';
import React, { useMemo, useState } from 'react';

import Img from 'gatsby-image';
import { Helmet } from 'react-helmet';
import SEO from '../components/seo';
import TrailTitle from '../components/TrailTitle';
import { graphql } from 'gatsby';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { SITE, DAY_TO_SCHEMA } from '../constants/site';

// "08.00" -> "08:00" untuk format waktu schema.org.
const toClock = (t) => (t ? t.replace('.', ':') : undefined);

const fadeInUp = keyframes`
  0% {
    opacity  : 0;
    transform: translateY(24px) scale(0.96);
  }

  100% {
    opacity  : 1;
    transform: translateY(0) scale(1);
  }
`;

const DelayFade = styled(Col)`
  opacity   : 0;
  animation : ${fadeInUp} 0.5s ease-out ${(props) => props.order * 0.08}s both;

  & .doctor-card {
    transition: transform 0.25s ease, box-shadow 0.25s ease;
  }

  & .gatsby-image-wrapper img,
  & .gatsby-image-wrapper picture img {
    transition: transform 0.35s ease;
  }

  &:hover .doctor-card {
    transform : translateY(-4px);
    box-shadow: 0 10px 26px rgba(0, 0, 0, 0.12) !important;
  }
`;

// Foto dokter dibuat bulat & ukuran seragam, apa pun rasio aslinya.
const Avatar = styled.div`
  width       : 72px;
  height      : 72px;
  border-radius: 50%;
  overflow    : hidden;
  flex-shrink : 0;

  & .gatsby-image-wrapper {
    width : 72px !important;
    height: 72px !important;
  }

  & img {
    width     : 100% !important;
    height    : 100% !important;
    object-fit: cover;
  }
`;

// Daftar hari untuk filter jadwal dokter.
const DAYS = ['Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];

const getJadwal = (jadwal) => {
  return (jadwal?.value || [])
    .map((item) => ({
      hari: item.hari?.value,
      mulai: item.mulai?.value,
      selesai: item.selesai?.value,
    }))
    .filter((item) => item.hari)
    .sort((a, b) => {
      const byDay = DAYS.indexOf(a.hari) - DAYS.indexOf(b.hari);
      return byDay !== 0 ? byDay : (a.mulai || '').localeCompare(b.mulai || '');
    });
};

const DoctorsPage = ({ data }) => {
  const [name, setName] = useState('');
  const [day, setDay] = useState('');

  const placeholderFluid = data.placeholder?.childImageSharp?.fluid;

  const doctors = useMemo(
    () =>
      data.doctors.edges.map(({ node }) => {
        const jadwal = getJadwal(node.jadwal);
        return {
          cockpitId: node.cockpitId,
          name: node.name?.value || '',
          service: node.service?.value?.display || '',
          jadwal,
          photo: node.photo?.value?.childImageSharp?.fixed,
          days: [...new Set(jadwal.map((j) => j.hari))],
        };
      }),
    [data.doctors.edges]
  );

  const filtered = useMemo(() => {
    const q = name.trim().toLowerCase();
    return doctors.filter((doctor) => {
      const matchName = q === '' || doctor.name.toLowerCase().includes(q);
      const matchDay = day === '' || doctor.days.includes(day);
      return matchName && matchDay;
    });
  }, [doctors, name, day]);

  // AEO: daftar dokter + jam praktik sebagai structured data (Physician).
  const doctorsSchema = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Dokter RSGM IIK Bhakti Wiyata',
      itemListElement: doctors.map((d, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'Physician',
          name: d.name,
          ...(d.service ? { medicalSpecialty: d.service } : {}),
          worksFor: {
            '@type': 'Dentist',
            '@id': `${SITE.url}/#organization`,
            name: SITE.legalName,
          },
          ...(d.jadwal.length
            ? {
              hoursAvailable: d.jadwal.map((j) => ({
                '@type': 'OpeningHoursSpecification',
                dayOfWeek: DAY_TO_SCHEMA[j.hari] || j.hari,
                ...(toClock(j.mulai) ? { opens: toClock(j.mulai) } : {}),
                ...(toClock(j.selesai) ? { closes: toClock(j.selesai) } : {}),
              })),
            }
            : {}),
        },
      })),
    }),
    [doctors]
  );

  return (
    <>
      <SEO
        title='Dokter Kami'
        pathname='/doctors'
        description='Cari dokter gigi umum dan spesialis di RSGM IIK Bhakti Wiyata Kediri lengkap dengan jadwal praktik per hari.'
      />
      <Helmet>
        <script type='application/ld+json'>
          {JSON.stringify(doctorsSchema)}
        </script>
      </Helmet>
      <Img fluid={data.cover.childImageSharp.fluid} />
      <Row
        css={{
          minHeight: '75vh',
          backgroundImage: `url(${data.bg.childImageSharp.fixed.src})`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'top right',
        }}
        className='pt-5'>
        <Col
          xs={'12'}
          sm={'5'}
          className='d-flex flex-column align-items-center align-content-center'>
          <TrailTitle open className='pb-2 mb-5'>
            <div>Cari Dokter</div>
            <div>RSGM IIK Bhakti Wiyata</div>
          </TrailTitle>
        </Col>

        <Col xs={'12'} sm={{ span: '10', offset: 1 }} className='py-3'>
          <Form className='mb-4'>
            <Row>
              <Col xs={'12'} md={'8'} className='mb-2 mb-md-0'>
                <Form.Control
                  type='text'
                  placeholder='Ketik nama dokter...'
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Col>
              <Col xs={'12'} md={'4'}>
                <Form.Control
                  as='select'
                  value={day}
                  onChange={(e) => setDay(e.target.value)}>
                  <option value=''>Semua Hari</option>
                  {DAYS.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </Form.Control>
              </Col>
            </Row>
          </Form>

          <Row>
            {filtered.map((doctor, idx) => (
              <DelayFade
                md={'4'}
                key={`${day}-${doctor.cockpitId}`}
                order={idx}
                className='mb-3'>
                <div className='doctor-card d-flex p-3 bg-white rounded shadow-sm h-100'>
                  <Avatar className='mr-3 bg-secondary'>
                    {doctor.photo ? (
                      <Img fixed={doctor.photo} />
                    ) : (
                      placeholderFluid && (
                        <Img fluid={placeholderFluid} />
                      )
                    )}
                  </Avatar>
                  <div css={{ minWidth: 0 }}>
                    <strong className='d-block'>
                      {doctor.name}
                    </strong>
                    {doctor.service && (
                      <div className='text-muted small'>
                        {doctor.service}
                      </div>
                    )}
                    <hr className='my-2' />
                    <div className='small text-secondary mb-1'>
                      Tersedia:
                    </div>
                    {doctor.jadwal.length > 0 ? (
                      <ul className='list-unstyled mb-0'>
                        {doctor.jadwal.map((j, i) => (
                          <li key={i} className='small'>
                            <span className='font-weight-bold'>
                              {j.hari}
                            </span>
                            {j.mulai && j.selesai && (
                              <span className='text-primary'>
                                {' '}
                                {j.mulai}–{j.selesai} WIB
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className='small text-muted'>
                        Jadwal belum tersedia
                      </div>
                    )}
                  </div>
                </div>
              </DelayFade>
            ))}
          </Row>

          {filtered.length === 0 && (
            <div className='d-flex justify-content-center align-items-center p-5 bg-secondary'>
              Tidak ada dokter yang sesuai dengan pencarian.
            </div>
          )}
        </Col>
      </Row>
    </>
  );
};

export const query = graphql`
  {
    bg: file(name: { eq: "bg-ring" }) {
      childImageSharp {
        fixed(toFormat: WEBP) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    }
    cover: file(name: { eq: "bg-doctors" }) {
      childImageSharp {
        fluid(maxWidth: 1920, toFormat: WEBP) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    doctors: allCockpitDoctors(filter: { display: { value: { eq: true } } }) {
      edges {
        node {
          cockpitId
          name {
            value
          }
          service {
            value {
              display
            }
          }
          jadwal {
            value {
                hari {
                value
                }
                mulai {
                value
                }
                selesai {
                value
                }
            }
          }
          photo {
            value {
              childImageSharp {
                fixed(
                  width: 150
                  height: 150
                  cropFocus: NORTH
                  quality: 100
                  toFormat: WEBP
                ) {
                  ...GatsbyImageSharpFixed_withWebp
                }
              }
            }
          }
        }
      }
    }
    placeholder: file(name: { eq: "dentist-placeholder" }) {
        childImageSharp {
            fluid(maxWidth: 150, toFormat: WEBP) {
            ...GatsbyImageSharpFluid_withWebp
            }
        }
    }
  }
`;

export default DoctorsPage;